/** Formato único de erro da API: `code` estável em inglês, `message` em português. */
export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export const ERROR_MESSAGES = {
  VALIDATION_FAILED: 'Alguns campos estão inválidos.',
  UNAUTHENTICATED: 'Você precisa entrar na sua conta.',
  INVALID_CREDENTIALS: 'E-mail ou senha incorretos.',
  TOO_MANY_ATTEMPTS: 'Muitas tentativas. Aguarde um minuto e tente de novo.',
  TOTP_REQUIRED: 'Informe o código do seu aplicativo autenticador.',
  INVALID_TOTP: 'Código do autenticador inválido.',
  INVALID_TOKEN: 'Link inválido ou expirado.',
  REFRESH_REUSED: 'Sua sessão foi encerrada por segurança. Entre novamente.',
  EMAIL_IN_USE: 'Já existe uma conta com este e-mail.',
  TENANT_REQUIRED: 'Escolha um negócio para continuar.',
  TENANT_FORBIDDEN: 'Você não tem acesso a este negócio.',
  PERMISSION_DENIED: 'Seu papel não permite esta ação.',
  MODULE_NOT_ENABLED: 'Este módulo não está ativo no seu plano.',
  MODULE_READ_ONLY: 'Este módulo está em modo somente leitura. Regularize a assinatura para voltar a editar.',
  PLAN_LIMIT_REACHED: 'Você atingiu o limite do seu plano.',
  NOT_FOUND: 'Não encontramos o que você procurou.',
  CONFLICT: 'Este registro conflita com outro já existente.',
  IDEMPOTENCY_IN_PROGRESS: 'Esta requisição ainda está sendo processada.',
  INTERNAL_ERROR: 'Algo deu errado do nosso lado. Tente de novo em instantes.',
  INVALID_DOCUMENT: 'CPF ou CNPJ inválido.',
  INVALID_ORDER_TRANSITION: 'Esta mudança de status não é permitida para o pedido.',
  INSUFFICIENT_STOCK: 'Estoque insuficiente.',
  INSUFFICIENT_CREDITS: 'Saldo de créditos insuficiente.',
  MODULE_DEPENDENCY: 'Outro módulo ativo depende deste.',
  WEBHOOK_INVALID: 'Assinatura do webhook inválida.',
  UNKNOWN_BUNDLE: 'Pacote de módulos não encontrado.',
  MODULE_ALREADY_ACTIVE: 'Este módulo já está no seu plano.',
  MODULE_NOT_IN_PLAN: 'Este módulo não está no seu plano.',
  SUBSCRIPTION_ALREADY_ACTIVE: 'Sua assinatura já está ativa.',
  SUBSCRIPTION_REQUIRED: 'Assine um plano para continuar.',
  SUBSCRIPTION_CANCELED: 'Sua assinatura foi cancelada.',
  PAYMENT_FAILED: 'O pagamento não foi aprovado. Confira os dados e tente de novo.',
  PAYMENT_PROVIDER_ERROR: 'Não conseguimos falar com o meio de pagamento. Tente de novo em instantes.',
  STORE_NOT_FOUND: 'Loja não encontrada.',
  STORE_NOT_PUBLISHED: 'Esta loja ainda não foi publicada.',
  STORE_UNAVAILABLE: 'Loja temporariamente indisponível.',
  DOMAIN_IN_USE: 'Este domínio já está em uso.',
  DOMAIN_NOT_VERIFIED: 'O domínio ainda não foi verificado.',
  INVALID_THEME_CONFIG: 'A configuração do tema é inválida.',
  CREDITS_MIN_PURCHASE: 'O valor mínimo de compra de créditos não foi atingido.',
  CHECKOUT_UNAVAILABLE: 'A loja não está recebendo pedidos agora.',
  CART_NOT_FOUND: 'Seu carrinho expirou. Adicione os produtos de novo.',
  CART_EMPTY: 'Seu carrinho está vazio.',
  PRODUCT_UNAVAILABLE: 'Este produto não está disponível.',
  COUPON_INVALID: 'Cupom inválido ou expirado.',
  SHIPPING_QUOTE_REQUIRED: 'Calcule o frete de novo para continuar.',
  ZIP_NOT_FOUND: 'Não encontramos este CEP.',
  SUBSCRIPTION_NEEDS_SERVICE: 'Só serviços podem ser vendidos por assinatura.',
  PRODUCT_IS_SUBSCRIPTION: 'Este serviço é vendido por assinatura. Use o botão "Assinar" na página dele.',
  PRODUCT_NOT_SUBSCRIPTION: 'Este produto não é vendido por assinatura.',
  STORE_SUBSCRIPTION_INVALID_STATE: 'Esta assinatura já foi cancelada.',
  STORE_LOGIN_INVALID_CODE: 'Código inválido ou expirado. Peça um novo.',
  STORE_CUSTOMER_UNAUTHENTICATED: 'Entre com seu e-mail para ver suas assinaturas.',
  BOOKING_NEEDS_SERVICE: 'Só serviços podem ser agendados. Marque o produto como serviço.',
  BOOKING_DURATION_REQUIRED: 'Informe quanto tempo dura cada atendimento.',
  BOOKING_QUOTA_REQUIRED: 'Informe quantos atendimentos a assinatura dá direito em cada semana ou mês.',
  BOOKING_QUOTA_PERIOD_INVALID: 'Na cobrança semanal, os atendimentos são contados por semana.',
  BOOKING_SCHEDULE_REQUIRED: 'Escolha como você quer marcar os atendimentos.',
  BOOKING_SCHEDULE_INVALID: 'Os horários escolhidos não servem para esta assinatura. Confira e tente de novo.',
  BOOKING_QUOTA_EXCEEDED: 'Você já usou todos os atendimentos deste período da assinatura.',
  BOOKING_OUTSIDE_PAID_PERIOD: 'Este dia ainda não está coberto pela assinatura. Escolha um dia até o fim do período pago.',
  BOOKING_PLAN_INACTIVE: 'Esta assinatura não está ativa para marcar atendimentos.',
  AGENDA_UNAVAILABLE: 'Esta loja não está recebendo agendamentos online no momento.',
  POS_DOCUMENT_REQUIRED: 'Informe o CPF ou CNPJ de quem vai pagar. O Pix e o cartão exigem.',
  POS_DISCOUNT_TOO_HIGH: 'O desconto não pode ser maior que o valor dos itens.',
  BOOKING_ALREADY_CHARGED: 'Este atendimento já tem uma cobrança. Abra o pedido dele.',
  PRODUCT_IS_BOOKING: 'Este serviço é agendado. Use o botão "Agendar" na página dele.',
  PRODUCT_NOT_BOOKABLE: 'Este serviço não é agendado pela agenda online.',
  BOOKING_DISABLED: 'Esta loja não está recebendo agendamentos online no momento.',
  BOOKING_SLOT_UNAVAILABLE: 'Este horário não está mais disponível. Escolha outro.',
  BOOKING_TIME_REQUIRED: 'Escolha um horário.',
  BOOKING_INVALID_STATE: 'Este agendamento já foi cancelado.',
  BOOKING_PAYMENT_REQUIRED: 'Este serviço é pago na hora de agendar. Informe o CPF e pague com Pix.',
  BOOKING_NOT_PENDING: 'Este agendamento não está esperando confirmação.',
  BOOKING_ALREADY_STARTED: 'Este agendamento já começou e não pode mais ser cancelado por aqui. Fale com a loja.',
  PAYMENT_METHOD_UNAVAILABLE: 'Esta forma de pagamento não está disponível nesta loja.',
  PAYMENT_ACCOUNT_EXISTS: 'A conta de recebimento já foi criada.',
  PAYMENT_ACCOUNT_REQUIRED: 'Crie a conta de recebimento para continuar.',
  PAYMENT_ACCOUNT_NOT_APPROVED: 'A conta de recebimento ainda está em análise. Os saques ficam liberados quando ela for aprovada.',
  ACCOUNT_DOCUMENT_NEEDS_LINK: 'Este documento é enviado pelo passo a passo de verificação, não por arquivo.',
  ACCOUNT_DOCUMENT_INVALID_FILE: 'Envie uma foto (JPG ou PNG) ou um PDF de até 10 MB.',
  ACCOUNT_DOCUMENT_NOT_FOUND: 'O meio de pagamento não pede mais este documento. Atualize a página.',
  PAYOUT_DESTINATION_REQUIRED: 'Cadastre a chave Pix ou a conta bancária para sacar.',
  PAYOUT_INSUFFICIENT_BALANCE: 'O valor é maior que o saldo disponível.',
  PAYOUT_BELOW_FEE: 'O valor do saque precisa ser maior que a taxa da transferência.',
  PAYOUT_IN_PROGRESS: 'Já existe um saque sendo enviado. Espere ele terminar para pedir outro.',
  SHIPPING_NOT_CONNECTED: 'Conecte sua conta do Melhor Envio para gerar etiquetas.',
  SHIPPING_PROVIDER_ERROR: 'Não conseguimos falar com a transportadora. Tente de novo em instantes.',
  REFUND_EXCEEDS_PAID: 'O valor do reembolso é maior que o valor pago.',
  FISCAL_NOT_CONFIGURED: 'Termine a configuração das notas fiscais para emitir.',
  FISCAL_VALIDATION_FAILED: 'Alguns dados da nota precisam ser corrigidos antes de emitir.',
  FISCAL_ALREADY_ISSUED: 'Este pedido já tem nota fiscal autorizada.',
  FISCAL_PROVIDER_ERROR: 'Não conseguimos falar com o emissor de notas. Tente de novo em instantes.',
  FISCAL_INVALID_STATE: 'Esta ação não é permitida para a nota neste status.',
  FISCAL_HOMOLOGATION_REQUIRED: 'Emita uma nota de teste em homologação antes de ativar a produção.',
  CERTIFICATE_INVALID: 'Não conseguimos abrir o certificado. Confira o arquivo .pfx e a senha.',
  CERTIFICATE_MISMATCH: 'O certificado não é do CNPJ deste negócio.',
  CERTIFICATE_EXPIRED: 'O certificado está vencido.',
  WHATSAPP_NOT_CONNECTED: 'Conecte o número de WhatsApp do negócio para enviar mensagens.',
  WHATSAPP_CONNECT_FAILED: 'Não conseguimos concluir a conexão com o WhatsApp. Refaça o passo a passo da Meta.',
  WHATSAPP_NUMBER_IN_USE: 'Este número de WhatsApp já está conectado a outro negócio.',
  WHATSAPP_WINDOW_CLOSED: 'Faz mais de 24 horas que o cliente escreveu. Pelo WhatsApp, só dá para responder com um modelo aprovado.',
  WHATSAPP_TEMPLATE_NOT_APPROVED: 'Este modelo ainda não foi aprovado pela Meta.',
  WHATSAPP_TEMPLATE_PARAMS: 'Preencha todas as variáveis do modelo.',
  INBOX_CHANNEL_INACTIVE: 'Este canal está desconectado.',
  CHAT_UNAVAILABLE: 'O chat desta loja não está disponível.',
  CHAT_SESSION_INVALID: 'Sua sessão de chat expirou. Recarregue a página.',
  ADS_NOT_CONNECTED: 'Conecte a página do Facebook do negócio para anunciar.',
  ADS_CONNECT_FAILED: 'Não conseguimos conectar sua página. Confira se você é administrador dela e tente de novo.',
  ADS_STOREFRONT_UNPUBLISHED: 'Publique sua loja antes de anunciar com destino a ela.',
  ADS_WHATSAPP_REQUIRED: 'Conecte o WhatsApp no Atendimento ou informe o número na loja para receber mensagens pelo anúncio.',
  ADS_INVALID_TRANSITION: 'Esta ação não é permitida no status atual da campanha.',
  ADS_TEMPLATE_MISSING: 'Ainda não temos um modelo de campanha para este segmento e objetivo.',
  ADS_PRODUCT_UNAVAILABLE: 'Um dos produtos escolhidos não está ativo ou não tem preço.',
  ADS_PLATFORM_ERROR: 'A Meta não aceitou a operação agora. Tente de novo em instantes.',
} as const;

export type ErrorCode = keyof typeof ERROR_MESSAGES;
